import { useState, useEffect, useRef } from 'react';

// Track hydration state to prevent DOMParser mismatches during SSR/hydration
let isHydrated = false;
let hydrationCallbacks: Set<() => void> = new Set();

export const getIsHydrated = (): boolean => isHydrated;

const notifyHydrationComplete = () => {
  hydrationCallbacks.forEach((callback) => callback());
};

export const onHydrationComplete = (callback: () => void): (() => void) => {
  hydrationCallbacks.add(callback);
  return () => {
    hydrationCallbacks.delete(callback);
  };
};

export const isHydrating = (): boolean => {
  if (typeof window === 'undefined') return true;
  if (isHydrated) return false;

  const serializedStateElement = document.querySelector(
    'script[type="x-mofa/serialized-states"]'
  );
  const hasSerializedState = Boolean(serializedStateElement);

  if (hasSerializedState && !isHydrated) {
    setTimeout(() => {
      isHydrated = true;
      notifyHydrationComplete();
    }, 100);
    return true;
  }

  if (!hasSerializedState) {
    isHydrated = true;
    notifyHydrationComplete();
    return false;
  }

  return true;
};

export const useHydrationStatus = (): boolean => {
  const [hydrated, setHydrated] = useState(() => {
    if (typeof window === 'undefined') return false;
    if (getIsHydrated()) return true;
    const hasSerializedState = Boolean(
      document.querySelector('script[type="x-mofa/serialized-states"]')
    );
    return !hasSerializedState;
  });

  const updateScheduledRef = useRef(false);

  useEffect(() => {
    if (
      !hydrated &&
      typeof window !== 'undefined' &&
      getIsHydrated() &&
      !updateScheduledRef.current
    ) {
      updateScheduledRef.current = true;
      setHydrated(true);
      return;
    }

    if (hydrated) {
      updateScheduledRef.current = false;
      return;
    }

    const checkHydration = () => {
      if (getIsHydrated() && !hydrated) {
        setHydrated(true);
      }
    };

    const unsubscribe = onHydrationComplete(() => {
      setHydrated(true);
    });

    const intervalId = setInterval(checkHydration, 100);

    return () => {
      unsubscribe();
      clearInterval(intervalId);
    };
  }, [hydrated]);

  return hydrated;
};
